import {
  useEffect,
  useMemo,
  useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Typography
} from '@mui/material';
import Autocomplete from '../components/SearchableAutocomplete';
import Select from '../components/SearchableSelect';
import { getAllStanze } from '../APIs/RoomsAPI';
import { aggiornaTavolo, getAllTavoli, getTavoloById, updateCompatibleSetups } from '../APIs/TablesAPI';
import { getAllSetups } from '../APIs/SetupsAPI';
import { useTranslation } from 'react-i18next';
import { getTenantTranslation } from '../utils/getTenantTranslation';
import { useSnackbar } from '../contexts/SnackBarContext';

const ModalEditTavolo = ({ open, tavolo, onClose, onSuccess }) => {
  const [nome, setNome] = useState("");
  const [note, setNote] = useState("");
  const [roomId, setRoomId] = useState("");
  const [maxPosti, setMaxPosti] = useState(tavolo?.maxSeats);
  const [stanze, setStanze] = useState([]);
  const [tavoli, setTavoli] = useState([]);
  const [setups, setSetups] = useState([]);
  const [parentTableId, setParentTableId] = useState("");
  const [compatibleSetupIds, setCompatibleSetupIds] = useState([]);
  const [errTavoli, setErrTavoli] = useState(false)

  const { t } = useTranslation()
  const tenantType = localStorage.getItem('tenantType');
  const isConferenceTenant = String(tenantType || '').toUpperCase() === 'SALE_CONGRESSI';
  const { showSnackbar } = useSnackbar();
  const stanzeAttive = useMemo(() => stanze.filter((stanza) => stanza?.active !== false), [stanze]);

  useEffect(() => {
    if (open) {
      const token = localStorage.getItem('accessToken');
      const tenant = localStorage.getItem('tenantId');
      const applyTable = (table) => {
        setNome(table?.name || "");
        setNote(table?.note || "");
        setRoomId(table?.roomId || "");
        setMaxPosti(table?.maxSeats || 0);
        setParentTableId(table?.parentTableId || "");
        setCompatibleSetupIds((table?.compatibleSetups || []).map((item) => item.setupId));
      };
      applyTable(tavolo);
      if (tavolo?.id) {
        getTavoloById(tavolo.id, token, tenant)
          .then(applyTable)
          .catch(() => {});
      }
      getAllStanze(token, tenant).then(data => setStanze(data)).catch(err => console.error(err));
      if (isConferenceTenant) {
        getAllTavoli(token, tenant, { noCache: true }).then(data => setTavoli(data)).catch(err => console.error(err));
        getAllSetups(token, tenant).then(data => setSetups(data)).catch(err => console.error(err));
      }
    }
  }, [open, tavolo, isConferenceTenant]);

  useEffect(() => {
    if (!open || !roomId) return;
    const roomIsActive = stanze.some((stanza) => stanza.id === roomId && stanza?.active !== false);
    if (!roomIsActive) setRoomId("");
  }, [open, roomId, stanze]);

  const handleSubmit = async () => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    const maxSeats = parseInt(maxPosti)

    setErrTavoli(false)

    if (!tavolo.id || !nome || !roomId) return;

    try {
      await aggiornaTavolo(tavolo.id, { name: nome, note, maxSeats, roomId, parentTableId: parentTableId || null }, token, tenant);
      if (isConferenceTenant) {
        await updateCompatibleSetups(tavolo.id, compatibleSetupIds, token, tenant);
      }
      window.dispatchEvent(new Event('map-data-changed'));
      if (onSuccess) await onSuccess();
      handleClose()

    } catch (err) {
      setErrTavoli(true)
      handleAlert(t("modals_alert.error_editTable")+ err.message);
    }
  };

  const handleClose = () => {
    setNome(tavolo?.name);
    setNote(tavolo?.note || "");
    setRoomId(tavolo?.roomId);
    setMaxPosti(tavolo?.maxSeats);
    setParentTableId(tavolo?.parentTableId || "");
    setCompatibleSetupIds((tavolo?.compatibleSetups || []).map((item) => item.setupId));
    setErrTavoli(false)
    onClose();
  };

  function handleAlert(msg, type) {
    showSnackbar(msg, type)
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{getTenantTranslation(t, tenantType, 'edit_table')}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label={getTenantTranslation(t, tenantType, "table_name")}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="room-label">{getTenantTranslation(t, tenantType, 'booking.room')}</InputLabel>
          <Select
            labelId="room-label"
            value={roomId}
            label={getTenantTranslation(t, tenantType, 'booking.room')}
            onChange={(e) => setRoomId(e.target.value)}
          >
            {stanzeAttive.map((stanza) => (
              <MenuItem key={stanza.id} value={stanza.id}>
                {stanza.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          type='number'
          margin="normal"
          label={getTenantTranslation(t, tenantType, "seatNumber")}
          value={parseInt(maxPosti)}
          onChange={(e) => setMaxPosti(e.target.value)}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          margin="normal"
          label={t('notes')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 1000 }}
        />
        {isConferenceTenant && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel id="parent-table-label">{t("parentRoomOptional")}</InputLabel>
              <Select
                labelId="parent-table-label"
                value={parentTableId}
                label={t("parentRoomOptional")}
                onChange={(e) => setParentTableId(e.target.value)}
              >
                <MenuItem value="">{t("noParentRoom")}</MenuItem>
                {tavoli
                  .filter((item) => item.id !== tavolo?.id)
                  .map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
              </Select>
              <Typography variant="caption" color="text.secondary">
                {t("parentRoomHelp")}
              </Typography>
            </FormControl>
            <Autocomplete
              multiple
              options={setups.filter((setup) => setup.active !== false)}
              value={setups.filter((setup) => compatibleSetupIds.includes(setup.id))}
              onChange={(_, value) => setCompatibleSetupIds(value.map((item) => item.id))}
              getOptionLabel={(option) => option?.name || ""}
              renderInput={(params) => <TextField {...params} margin="normal" label={t("Allestimenticompatibili")} />}
            />
            <Typography variant="caption" color="text.secondary">
              {t("durante")}
            </Typography>
          </>
        )}
        <Typography color="red">{errTavoli ? getTenantTranslation(t, tenantType, 'error_number_of_seats') : ""}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{getTenantTranslation(t, tenantType, 'cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!nome || !roomId || maxPosti < 1 || !maxPosti}>
          {getTenantTranslation(t, tenantType, 'save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEditTavolo;
